import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatBRL } from "@/lib/api";
import { Loader2, CheckCircle2, Clock, XCircle, Copy, Check, ArrowLeft, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_encontrao-renascer/artifacts/xf5sr3g8_encontrao_lg.png";
const POLLING_INTERVAL_MS = 15000;
const COPY_FEEDBACK_MS = 2200;

const STATUS_META = {
  paid: {
    label: "Pagamento confirmado",
    accent: "bg-green-50 text-green-800 border-green-200",
    Icon: CheckCircle2,
    description: "Seu pagamento foi confirmado pela organização. Guarde este identificador — ele é seu comprovante.",
  },
  pending: {
    label: "Aguardando pagamento",
    accent: "bg-amber-50 text-amber-900 border-amber-200",
    Icon: Clock,
    description: "Estamos aguardando a confirmação do pagamento. Assim que a organização identificar o valor recebido, o status é atualizado automaticamente aqui.",
  },
  cancelled: {
    label: "Cancelado",
    accent: "bg-red-50 text-red-800 border-red-200",
    Icon: XCircle,
    description: "Este pedido foi cancelado. Se acredita que isso é um engano, entre em contato com a organização.",
  },
};

export default function TrackOrderPage() {
  const { txid } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const res = await api.get(`/track/${txid}`);
      setData(res.data);
      setError("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Pedido não encontrado.");
      setData(null);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [txid]);

  useEffect(() => {
    load();
    const t = setInterval(() => load(true), POLLING_INTERVAL_MS);
    return () => clearInterval(t);
  }, [load]);

  const meta = useMemo(() => STATUS_META[data?.status] || STATUS_META.pending, [data]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(txid);
      setCopied(true);
      toast.success("Identificador copiado!");
      setTimeout(() => setCopied(false), COPY_FEEDBACK_MS);
    } catch {
      toast.error("Não foi possível copiar.");
    }
  };

  const createdAt = data?.created_at ? new Date(data.created_at) : null;
  const createdLabel = createdAt
    ? createdAt.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : "";

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 to-orange-50/40">
      <header className="border-b border-stone-200 bg-white/90 backdrop-blur-md sticky top-0 z-20">
        <div className="max-w-3xl mx-auto flex items-center justify-between gap-3 px-4 py-4">
          <Link to="/" className="flex items-center gap-2" data-testid="track-home-link">
            <img src={LOGO_URL} alt="Renascer" className="h-10 w-10 object-contain" />
            <div className="leading-tight">
              <div className="text-xs uppercase tracking-widest text-orange-700 font-semibold">3º Encontrão</div>
              <div className="font-display text-base font-semibold text-stone-900">Acompanhar pedido</div>
            </div>
          </Link>
          <Link to="/" className="hidden sm:inline-flex items-center gap-1.5 text-sm text-stone-600 hover:text-stone-900" data-testid="track-back-link">
            <ArrowLeft className="h-4 w-4" /> Voltar
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-10">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-24 text-stone-500 gap-3">
            <Loader2 className="h-8 w-8 animate-spin" />
            <div className="text-sm">Buscando seu pedido...</div>
          </div>
        ) : error ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-8 text-center" data-testid="track-not-found">
            <XCircle className="h-10 w-10 text-red-500 mx-auto" />
            <h1 className="mt-3 font-display text-2xl font-bold text-red-800">Pedido não encontrado</h1>
            <p className="mt-2 text-sm text-red-700">{error}</p>
            <p className="mt-3 text-xs text-red-600">Identificador buscado: <strong className="font-mono">{txid}</strong></p>
            <Link to="/" className="mt-6 inline-block px-5 py-2.5 rounded-full bg-red-600 hover:bg-red-700 text-white font-semibold text-sm">Voltar à loja</Link>
          </div>
        ) : (
          <div className="space-y-6" data-testid="track-content">
            <div className={`rounded-2xl border p-6 ${meta.accent}`}>
              <div className="flex items-start gap-4">
                <meta.Icon className="h-10 w-10 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs uppercase tracking-widest font-semibold opacity-80">Status</div>
                  <h1 className="font-display text-3xl font-bold mt-1" data-testid="track-status-label">{meta.label}</h1>
                  <p className="text-sm mt-2 opacity-90">{meta.description}</p>
                </div>
                <button
                  type="button"
                  onClick={() => load(true)}
                  disabled={refreshing}
                  className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-current/20 text-xs font-semibold hover:bg-white/50 disabled:opacity-50"
                  data-testid="track-refresh-btn"
                >
                  <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
                  Atualizar
                </button>
              </div>
            </div>

            <div className="rounded-2xl border border-stone-200 bg-white p-6">
              <div className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Identificador do pagamento</div>
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <code className="font-mono text-sm sm:text-base bg-stone-100 px-3 py-2 rounded-lg text-stone-900 break-all" data-testid="track-txid">{data.txid}</code>
                <button
                  type="button"
                  onClick={handleCopy}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-stone-300 hover:bg-stone-50 text-xs font-semibold"
                  data-testid="track-copy-txid"
                >
                  {copied ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />}
                  {copied ? "Copiado" : "Copiar"}
                </button>
              </div>
              <p className="mt-2 text-xs text-stone-500">Este código aparece no seu extrato bancário quando o pagamento é feito via PIX. Guarde-o para consulta.</p>
              <div className="mt-4 grid sm:grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Comprador</div>
                  <div className="text-stone-900 mt-1" data-testid="track-customer">{data.customer_name}</div>
                </div>
                {createdLabel && (
                  <div>
                    <div className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Realizado em</div>
                    <div className="text-stone-900 mt-1">{createdLabel}</div>
                  </div>
                )}
                {data.kind === "subscription" && data.city && (
                  <div>
                    <div className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Cidade</div>
                    <div className="text-stone-900 mt-1">{data.city}</div>
                  </div>
                )}
                <div>
                  <div className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Tipo</div>
                  <div className="text-stone-900 mt-1">{data.kind === "subscription" ? "Inscrição presencial" : "Pedido da loja"}</div>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-stone-200 bg-white p-6">
              <div className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Itens</div>
              <ul className="mt-3 divide-y divide-stone-100" data-testid="track-items">
                {(data.items || []).map((it, i) => (
                  <li key={`${it.item_id || it.name}-${it.size || ""}-${it.variant || ""}-${i}`} className="py-3 flex items-start justify-between gap-3 text-sm">
                    <div className="min-w-0">
                      <div className="font-semibold text-stone-900 truncate">{it.name}</div>
                      <div className="text-xs text-stone-500 mt-0.5">
                        {it.quantity} × {formatBRL(it.price)}
                        {it.size ? ` · Tamanho ${it.size}` : ""}
                        {it.variant ? ` · ${it.variant}` : ""}
                      </div>
                    </div>
                    <div className="text-sm font-semibold text-stone-900 shrink-0">{formatBRL((it.price || 0) * (it.quantity || 0))}</div>
                  </li>
                ))}
              </ul>
              <div className="mt-4 pt-4 border-t border-stone-200 flex items-center justify-between">
                <div className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Total geral</div>
                <div className="font-display text-2xl font-bold brand-text" data-testid="track-total">{formatBRL(data.total || 0)}</div>
              </div>
            </div>

            <div className="text-center text-xs text-stone-500">
              Esta página atualiza automaticamente a cada 15 segundos. Você pode fechar e voltar quando quiser usando o mesmo link.
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
